import React, { useState } from 'react';
import { ChevronRightIcon, ChevronDownIcon, TagIcon, ArrowPathIcon, PencilIcon, TrashIcon, FolderPlusIcon, CheckIcon, BookmarkIcon as BookmarkOutlineIcon, DocumentTextIcon, VideoCameraIcon } from '@heroicons/react/24/outline';
import { BookmarkIcon as BookmarkSolidIcon } from '@heroicons/react/24/solid';
import EditBookmarkModal from './EditBookmarkModal';
import AddToCollectionModal from './AddToCollectionModal';

const MEDIA_TYPE_CONFIG = {
  pdf:   { label: 'PDF',   Icon: DocumentTextIcon, className: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400' },
  video: { label: 'Video', Icon: VideoCameraIcon,  className: 'bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-400' },
};

const MediaTypePill = ({ mediaType }) => {
  const config = MEDIA_TYPE_CONFIG[mediaType];
  if (!config) return null;
  const { label, Icon, className } = config;
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium flex-shrink-0 ${className}`}>
      <Icon className="h-3 w-3" />
      {label}
    </span>
  );
};

const BookmarkListRow = ({ bookmark, onDelete, onUpdate, onRetry, highlighted = false, selected = false, selectionActive = false, onSelect }) => {
  const [expanded, setExpanded] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCollectionModalOpen, setIsCollectionModalOpen] = useState(false);

  const handleEditSave = async (updatedData) => {
    if (onUpdate) await onUpdate(bookmark.bookmarkId, updatedData);
  };

  const formatDate = (dateString) =>
    new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

  const getStatusColor = (status) => {
    switch (status) {
      case 'processed':      return 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400';
      case 'processing':
      case 'content_fetched':
      case 'metadata_extracted':
      case 'analyzed':       return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400';
      case 'failed':         return 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400';
      default:               return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300';
    }
  };

  const getDomain = (url) => {
    try { return new URL(url).hostname.replace(/^www\./, ''); }
    catch { return url; }
  };

  const canRetry = ['failed', 'processed', 'processing'].includes(bookmark.status);

  return (
    <div
      id={`bookmark-${bookmark.bookmarkId}`}
      className={`border-b border-gray-200 dark:border-gray-700 last:border-b-0 transition-colors duration-300 group
        ${highlighted ? 'bg-primary-50 dark:bg-primary-900/20 ring-2 ring-inset ring-primary-400' : ''}
        ${selected ? 'bg-primary-50 dark:bg-primary-900/20 ring-2 ring-inset ring-primary-400' : ''}
      `}
    >
      {/* Collapsed row */}
      <div className="flex items-center px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
        {/* Checkbox — visible on hover or when selection is active */}
        {onSelect && (
          <button
            onClick={(e) => { e.stopPropagation(); onSelect(bookmark.bookmarkId); }}
            className={`flex-shrink-0 mr-2 w-4 h-4 rounded border-2 flex items-center justify-center transition-all
              ${selected ? 'bg-primary-600 border-primary-600' : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-500 hover:border-primary-400'}
              ${selectionActive || selected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}
            `}
            aria-label={selected ? 'Deselect bookmark' : 'Select bookmark'}
          >
            {selected && <CheckIcon className="h-2.5 w-2.5 text-white" />}
          </button>
        )}

        {/* Expand chevron */}
        <button
          onClick={() => setExpanded(e => !e)}
          className="flex-shrink-0 mr-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
          aria-label={expanded ? 'Collapse' : 'Expand'}
        >
          {expanded
            ? <ChevronDownIcon className="h-4 w-4" />
            : <ChevronRightIcon className="h-4 w-4" />
          }
        </button>

        {/* Title — opens site in new tab */}
        <a
          href={bookmark.url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex-1 min-w-0 font-medium text-gray-900 dark:text-gray-100 hover:text-primary-600 dark:hover:text-primary-400 transition-colors truncate mr-3"
          title={bookmark.url}
        >
          {bookmark.title || 'Untitled'}
        </a>

        {/* Domain — hidden on small screens */}
        <span className="hidden sm:block flex-shrink-0 text-xs text-gray-400 dark:text-gray-500 mr-3 w-32 truncate text-right">
          {getDomain(bookmark.url)}
        </span>

        {/* Media type indicator */}
        {bookmark.mediaType && (
          <span className="hidden sm:block mr-2">
            <MediaTypePill mediaType={bookmark.mediaType} />
          </span>
        )}

        {/* Status badge */}
        <span className={`flex-shrink-0 px-2 py-0.5 rounded-full text-xs font-medium mr-3 ${getStatusColor(bookmark.status)}`}>
          {bookmark.status || 'pending'}
        </span>

        {/* Date — hidden on small screens */}
        <span className="hidden md:block flex-shrink-0 text-xs text-gray-400 dark:text-gray-500 mr-3 w-24 text-right">
          {formatDate(bookmark.createdAt)}
        </span>

        {/* Actions */}
        <div className="flex-shrink-0 flex items-center space-x-2">
          <button
            onClick={() => onUpdate && onUpdate(bookmark.bookmarkId, { readLater: !bookmark.readLater })}
            className={`transition-colors ${bookmark.readLater ? 'text-amber-500 hover:text-amber-600' : 'text-gray-400 hover:text-amber-400'}`}
            title={bookmark.readLater ? 'Remove from reading list' : 'Save to reading list'}
          >
            {bookmark.readLater
              ? <BookmarkSolidIcon className="h-4 w-4" />
              : <BookmarkOutlineIcon className="h-4 w-4" />
            }
          </button>
          <button
            onClick={() => setIsCollectionModalOpen(true)}
            className="text-gray-400 hover:text-primary-600 transition-colors"
            title="Add to collection"
          >
            <FolderPlusIcon className="h-4 w-4" />
          </button>
          <button
            onClick={() => setIsEditModalOpen(true)}
            className="text-gray-400 hover:text-primary-600 transition-colors"
            title="Edit"
          >
            <PencilIcon className="h-4 w-4" />
          </button>
          <button
            onClick={() => onDelete(bookmark.bookmarkId)}
            className="text-gray-400 hover:text-red-600 transition-colors"
            title="Delete"
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div className="px-11 pb-4 space-y-3">
          {/* Date on mobile (hidden in collapsed row) */}
          <p className="md:hidden text-xs text-gray-400 dark:text-gray-500">{formatDate(bookmark.createdAt)}</p>

          {bookmark.description && (
            <p className="text-sm text-gray-600 dark:text-gray-400">{bookmark.description}</p>
          )}

          {bookmark.summary && (
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-md p-3">
              <p className="text-sm text-gray-700 dark:text-gray-300">{bookmark.summary}</p>
            </div>
          )}

          {bookmark.notes && (
            <div className="bg-amber-50 dark:bg-amber-900/20 border-l-2 border-amber-300 dark:border-amber-700 rounded-r-md px-3 py-2">
              <p className="text-xs font-medium text-amber-700 dark:text-amber-400 mb-0.5">Notes</p>
              <p className="text-sm text-amber-900 dark:text-amber-300 whitespace-pre-wrap">{bookmark.notes}</p>
            </div>
          )}

          {bookmark.tags && bookmark.tags.length > 0 && (
            <div className="flex items-start space-x-1">
              <TagIcon className="h-4 w-4 mt-0.5 flex-shrink-0 text-gray-400" />
              <div className="flex flex-wrap gap-1">
                {bookmark.tags.map((tag, i) => (
                  <span key={i} className="bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-300 px-2 py-0.5 rounded-full text-xs font-medium">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {bookmark.categories && bookmark.categories.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {bookmark.categories.map((cat, i) => (
                <span key={i} className="bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 px-2 py-0.5 rounded-full text-xs font-medium">
                  {cat}
                </span>
              ))}
            </div>
          )}

          {canRetry && onRetry && (
            <button
              onClick={() => onRetry(bookmark.bookmarkId)}
              className={`flex items-center space-x-1 text-xs px-2 py-1 rounded transition-colors ${
                bookmark.status === 'processed'
                  ? 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                  : 'text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30'
              }`}
            >
              <ArrowPathIcon className="h-3 w-3" />
              <span>{bookmark.status === 'processed' ? 'Reprocess' : 'Retry'}</span>
            </button>
          )}
        </div>
      )}

      <EditBookmarkModal
        bookmark={bookmark}
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSave={handleEditSave}
      />
      <AddToCollectionModal
        bookmarkId={bookmark.bookmarkId}
        isOpen={isCollectionModalOpen}
        onClose={() => setIsCollectionModalOpen(false)}
      />
    </div>
  );
};

export default BookmarkListRow;
